import { Alert, Box, Button, Modal, Text, Toast } from '@zillow/constellation-10';
import { useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useToast } from '@zillow/constellation-10';
import { CustomerAnalyticsMoodysWebApiHandler_getPremiumMoodysReport } from '@zg-rentals/ts-customer-analytics-client';
import {
  type HideCustomPdfSections,
  renderMarketTrendsPdf,
  renderPremiumMarketTrendsPdf,
} from '../../../utils/pdfRenderer';
import { logError } from '@zg-rentals/log-error';
import { httpClient } from '../../../httpClient';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitIndustryReportsDownloadEvent } from '../../../analytics/events';

interface DownloadModalProps {
  metroId: string;
  submarketId?: number;
  year: number;
  quarter: number;
  errorState: [boolean, (val: boolean) => void];
  isSignatureUser: boolean;
}

export const DownloadModal = ({
  metroId,
  submarketId,
  year,
  quarter,
  errorState,
  isSignatureUser,
}: DownloadModalProps) => {
  const { user } = useRentalManagerUser();
  const [isOpen, setIsOpen] = useState(false);
  const [hasRenderPdfError, setHasRenderPdfError] = errorState;

  const { enqueueToast } = useToast();

  const handleDownloadAndClose = () => {
    if (user) emitIndustryReportsDownloadEvent({ user });
    handleDownloadClick();
    handleClose();
  };

  const [loadingState, handleDownloadClick] = useAsyncFn(async () => {
    enqueueToast(<Toast tone="info">Downloading your market trends PDF.</Toast>);
    setIsOpen(false);
    const report = await CustomerAnalyticsMoodysWebApiHandler_getPremiumMoodysReport(
      {
        metroId,
        submarketId,
        year,
        quarter,
      },
      httpClient,
    ).catch((e) => {
      const errorMessage = DatarouterError.getDisplayMessage(e, 'Error fetching report, please refresh and try again.');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      logError({ error: e, context: 'IndustryReports getPremiumMoodysReport', errorType: 'MoodysPremiumApiFailure' });
    });

    if (!report) return;
    try {
      if (isSignatureUser) {
        await renderPremiumMarketTrendsPdf(
          report,
          {
            keyPerformanceIndicators: false,
            submarketInventory: false,
            rentTrends: false,
            vacancyTrends: false,
            marketInventory: false,
          } as HideCustomPdfSections,
          setHasRenderPdfError,
        );
      } else {
        await renderMarketTrendsPdf(report, setHasRenderPdfError);
      }
    } catch (e) {
      setHasRenderPdfError(true);
      logError({
        error: e,
        context: isSignatureUser
          ? 'IndustryReports renderPremiumMarketTrendsPdf'
          : 'IndustryReports renderMarketTrendsPdf',
        errorType: 'MoodysPdfRenderFailure',
      });
      const errorMessage = DatarouterError.getDisplayMessage(e, 'Failed to generate PDF. Please try again later.');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  }, [metroId, submarketId, year, quarter, setHasRenderPdfError, enqueueToast, isSignatureUser]);

  const hasLoadingError = !!loadingState.error;

  const handleClose = () => {
    setIsOpen(false);
  };

  const renderModalContent = () => {
    if (hasLoadingError || hasRenderPdfError) {
      return (
        <Alert tone="critical">
          {loadingState.error?.message || 'Error occurred during download, refresh the page and try again.'}
        </Alert>
      );
    }

    if (isSignatureUser) {
      return (
        <Box css={{ padding: '600' }}>
          <Box css={{ display: 'flex', marginTop: '800', justifyContent: 'space-between', gap: '600' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text css={{ fontWeight: 'bold', fontSize: 'sm' }}>Overview</Text>
              <Text>Zillow Market Overview</Text>
              <Text>Moody&apos;s Executive Briefing</Text>
              <Text>Key Performance Indicators</Text>
              <Text>Asking Rent & Vacancy Trends</Text>
              <Text>Submarket Performance</Text>
              <Text>Submarket Inventory</Text>
              <Text>Scenarios: Asking Rent per Unit</Text>
              <Text>Scenarios: Vacancy</Text>
              <Text>Scenarios: Inventory</Text>
            </Box>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text css={{ fontWeight: 'bold', fontSize: 'sm' }}>Performance</Text>
              <Text>Concessions & Effective Rent</Text>
              <Text>Annualized Construction/Absorption</Text>
              <Text>Quarterly Construction/Absorption</Text>
              <Text>Performance By Vintage</Text>
              <Text>Performance Unit Mix</Text>
              <Text css={{ fontWeight: 'bold', fontSize: 'sm', marginTop: '200' }}>Multifamily New Construction</Text>
              <Text>Multifamily New Construction Projects By Size</Text>
            </Box>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text css={{ fontWeight: 'bold', fontSize: 'sm' }}>Demographics</Text>
              <Text>Employment Change</Text>
              <Text>Population Change</Text>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box css={{ padding: '600' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
          <Text css={{ fontWeight: 'bold', fontSize: 'sm' }}>Report Contents</Text>
          <Text>Market Map</Text>
          <Text>Key Performance Indicators</Text>
          <Text>Asking Rent & Vacancy Trends</Text>
          <Text>Market Inventory</Text>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Button tone="brand" emphasis="filled" size="md" onClick={() => setIsOpen(true)}>
        Download
      </Button>

      <Modal
        open={isOpen}
        onOpenChange={(isModalOpen) => {
          if (!isModalOpen) handleClose();
        }}
        size={isSignatureUser ? 'lg' : 'md'}
        header={isSignatureUser ? 'Download Advanced Report' : 'Download Basic Report'}
        body={renderModalContent()}
        footer={
          <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: '400' }}>
            <Button tone="neutral" emphasis="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button tone="brand" emphasis="filled" onClick={handleDownloadAndClose} disabled={loadingState.loading}>
              Download
            </Button>
          </Box>
        }
      />
    </>
  );
};
